/**
 * 数据访问仓库（IndexedDB 上层）。页面/组件只依赖本层，不直接操作 Dexie。
 *
 * 阶段 2：种子动作库 + 设置（profile/injury/nutrition）+ 每日意图 + 饮食 + 体重 + 同步队列。
 * 训练会话/每组记录见 workoutRepo.ts。
 */
import { db } from "./db";
import { EXERCISE_LIBRARY } from "../data/exercises";
import {
  INITIAL_NUTRITION_TARGET,
  type BodyMeasurement,
  type DailyIntent,
  type DailyIntentType,
  type InjuryConstraint,
  type MealEntry,
  type NotificationSetting,
  type NutritionTarget,
  type ProgressionDecision,
  type SyncOperation,
  type SyncStatus,
  type TrainingTemplate,
  type UserProfile,
} from "@jilian/shared";
import { createDefaultTemplates } from "@jilian/core";
import type { CorrectionPref } from "@jilian/ai";
import { aggregatePrefs, prefFromCorrection } from "@jilian/ai";
import { uuid } from "../lib/ids";

const nowIso = () => new Date().toISOString();

// 默认初始配置（仅在空库初次启动时写入；禁止写入演示饮食/体重/假会话）
export const DEFAULT_PROFILE: UserProfile = {
  id: "user-default",
  name: "极练用户",
  sex: "male",
  birthDate: "1999-01-01",
  heightCm: 175,
  timezone: "Asia/Shanghai",
  preferredUnits: "metric",
  activityLevel: "sedentary",
  goalType: "muscle_gain_priority",
  targetBodyFatPercent: 15,
  targetDate: null,
  createdAt: nowIso(),
  updatedAt: nowIso(),
};

export const DEFAULT_INJURY: InjuryConstraint = {
  id: "injury-default",
  userId: "user-default",
  bodyPart: "right_foot_little_toe",
  side: "right",
  injuryType: "fracture",
  injuryDate: "2025-05-01",
  weightBearingLevel: "partial_weight_bearing",
  forefootRestriction: "limited",
  heelRestriction: "unrestricted",
  balanceDemandAllowed: "low_to_medium",
  ankleMovementAllowed: true,
  medicalClearanceStage: 2,
  notes: "右脚小脚趾骨折康复期，部分承重；前足受限。下肢复合动作为医疗保护加锁。",
  active: true,
  version: 1,
  createdAt: nowIso(),
  updatedAt: nowIso(),
};

export const DEFAULT_NUTRITION_TARGET: NutritionTarget = {
  id: "nt-default",
  userId: "user-default",
  effectiveFrom: "2025-06-01",
  calories: INITIAL_NUTRITION_TARGET.calories,
  proteinGrams: INITIAL_NUTRITION_TARGET.proteinGrams,
  fatGrams: INITIAL_NUTRITION_TARGET.fatGrams,
  carbsGrams: INITIAL_NUTRITION_TARGET.carbsGrams,
  reasonCodes: ["INITIAL_ESTIMATE"],
  calibrationWindowDays: 21,
  userAccepted: true,
  createdAt: nowIso(),
};

const SEED_VERSION = 7;

/** 首次初始化与迁移：同步全量动作库 + 初始化默认配置与模板（不写入任何演示饮食/体重）。 */
export async function ensureSeeded(): Promise<void> {
  const meta = await db.meta.get("seedVersion");
  const curVersion = meta ? Number(meta.value) : 0;

  // 始终保证动作库最新
  await db.exercises.bulkPut(EXERCISE_LIBRARY);

  // 初始化训练序列模板（若为空）
  const templateCount = await db.trainingTemplates.count();
  if (templateCount === 0) {
    const existingProfile = await db.meta.get("profile");
    const userId = (existingProfile?.value as UserProfile)?.id ?? DEFAULT_PROFILE.id;
    const defaultTemplates = createDefaultTemplates(userId);
    await db.trainingTemplates.bulkPut(defaultTemplates);
  }

  if (curVersion < SEED_VERSION) {
    // 清理历史演示假数据（2025 年遗留假数据）
    await db.meals.where("consumedAt").startsWith("2025").delete();
    await db.measurements.where("date").startsWith("2025").delete();

    // 初始化基础配置（仅当不存在时）
    const existingProfile = await db.meta.get("profile");
    if (!existingProfile) {
      await db.meta.put({ key: "profile", value: DEFAULT_PROFILE, updatedAt: nowIso() });
    }
    const existingInjury = await db.meta.get("injury");
    if (!existingInjury) {
      await db.meta.put({ key: "injury", value: DEFAULT_INJURY, updatedAt: nowIso() });
    }
    const existingNt = await db.meta.get("nutritionTarget");
    if (!existingNt) {
      await db.meta.put({ key: "nutritionTarget", value: DEFAULT_NUTRITION_TARGET, updatedAt: nowIso() });
    }
    const existingDays = await db.meta.get("targetTrainingDaysPerWeek");
    if (!existingDays) {
      await db.meta.put({ key: "targetTrainingDaysPerWeek", value: 4, updatedAt: nowIso() });
    }

    await db.meta.put({ key: "seedVersion", value: SEED_VERSION, updatedAt: nowIso() });
  }
}

// ---------------------------- 设置（profile / injury / nutrition / targets） ----------------------------
export interface AppSettings {
  profile: UserProfile;
  injury: InjuryConstraint;
  nutrition: NutritionTarget;
  targetTrainingDaysPerWeek: number;
}

export async function getSettings(): Promise<AppSettings | null> {
  const [p, i, n, t] = await Promise.all([
    db.meta.get("profile"),
    db.meta.get("injury"),
    db.meta.get("nutritionTarget"),
    db.meta.get("targetTrainingDaysPerWeek"),
  ]);
  if (!p?.value || !i?.value || !n?.value) {
    return null;
  }
  return {
    profile: p.value as UserProfile,
    injury: i.value as InjuryConstraint,
    nutrition: n.value as NutritionTarget,
    targetTrainingDaysPerWeek: typeof t?.value === "number" ? t.value : 4,
  };
}

export async function saveProfile(profile: UserProfile): Promise<void> {
  await db.meta.put({ key: "profile", value: profile, updatedAt: nowIso() });
}

export async function saveInjury(injury: InjuryConstraint): Promise<void> {
  await db.meta.put({ key: "injury", value: injury, updatedAt: nowIso() });
}

export async function saveNutritionTarget(target: NutritionTarget): Promise<void> {
  await db.meta.put({ key: "nutritionTarget", value: target, updatedAt: nowIso() });
}

export async function saveTargetTrainingDaysPerWeek(days: number): Promise<void> {
  await db.meta.put({ key: "targetTrainingDaysPerWeek", value: days, updatedAt: nowIso() });
}


// ---------------------------- 每日意图（DailyIntent） ----------------------------
const intentId = (userId: string, localDate: string) => `${userId}:${localDate}`;

export async function getTodayIntent(userId: string, localDate: string): Promise<DailyIntent | null> {
  return (await db.dailyIntent.get(intentId(userId, localDate))) ?? null;
}

export async function setTodayIntent(
  userId: string,
  localDate: string,
  timezone: string,
  intent: DailyIntentType,
  deviceId: string,
): Promise<DailyIntent> {
  const rec: DailyIntent = {
    id: intentId(userId, localDate),
    userId,
    localDate,
    timezone,
    intent,
    selectedAt: nowIso(),
    deviceId,
    updatedAt: nowIso(),
  };
  await db.dailyIntent.put(rec);
  return rec;
}

// ---------------------------- 饮食 ----------------------------
export async function listMeals(userId: string): Promise<MealEntry[]> {
  return db.meals.where("userId").equals(userId).sortBy("consumedAt");
}

export async function saveMeal(meal: MealEntry, syncStatus: SyncStatus = "local_only"): Promise<void> {
  await db.meals.put({ ...meal, syncStatus });
  await enqueueSyncEvent({
    userId: meal.userId,
    entityType: "meal",
    entityId: meal.id,
    operation: "upsert",
    entityVersion: 1,
    payload: meal,
  });
}

export async function deleteMeal(mealId: string, userId: string): Promise<void> {
  await db.meals.delete(mealId);
  await enqueueSyncEvent({
    userId,
    entityType: "meal",
    entityId: mealId,
    operation: "delete",
    entityVersion: 1,
    payload: { id: mealId },
  });
}

export async function saveMealEstimate(userId: string, meal: MealEntry): Promise<void> {
  // 阶段 5 接入 AI；此处仅保存修正后的记录。
  await db.meals.put(meal);
  await enqueueSyncEvent({
    userId,
    entityType: "meal",
    entityId: meal.id,
    operation: "upsert",
    entityVersion: 2,
    payload: meal,
  });
}

// ---------------------------- 体重 / 身体数据 ----------------------------
export async function listMeasurements(userId: string): Promise<BodyMeasurement[]> {
  return db.measurements.where("userId").equals(userId).sortBy("date");
}

export async function saveMeasurement(m: BodyMeasurement, syncStatus: SyncStatus = "local_only"): Promise<void> {
  await db.measurements.put({ ...m, syncStatus });
  await enqueueSyncEvent({
    userId: m.userId,
    entityType: "measurement",
    entityId: m.id,
    operation: "upsert",
    entityVersion: 1,
    payload: m,
  });
}

// ---------------------------- 同步元数据（meta：游标 / 冲突留档） ----------------------------
export async function getMetaNumber(key: string): Promise<number> {
  const row = await db.meta.get(key);
  const v = row?.value;
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

export async function getMetaJson<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key);
  return (row?.value as T | undefined) ?? fallback;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value, updatedAt: nowIso() });
}

/** 冲突留档（ADR-013 简化：服务端保留双版本 + 客户端展示依据；用户可标已处理）。 */
export interface LocalConflictRecord {
  entityType: string;
  entityId: string;
  resolution: string;
  reasonCodes: string[];
  createdAt: string;
  acknowledged: boolean;
}

export async function addLocalConflicts(records: LocalConflictRecord[]): Promise<void> {
  if (records.length === 0) return;
  const existing = await getMetaJson<LocalConflictRecord[]>("syncConflicts", []);
  const merged = [...records, ...existing].slice(0, 50); // 只留最近 50 条
  await setMeta("syncConflicts", merged);
}

export async function listLocalConflicts(): Promise<LocalConflictRecord[]> {
  return getMetaJson<LocalConflictRecord[]>("syncConflicts", []);
}

export async function acknowledgeConflict(entityType: string, entityId: string, createdAt: string): Promise<void> {
  const existing = await getMetaJson<LocalConflictRecord[]>("syncConflicts", []);
  await setMeta(
    "syncConflicts",
    existing.map((c) =>
      c.entityType === entityType && c.entityId === entityId && c.createdAt === createdAt
        ? { ...c, acknowledged: true }
        : c,
    ),
  );
}

// ---------------------------- 进阶决策落库（阶段 7：导出数据源；§12 用户可接受/覆盖） ----------------------------
export async function saveProgressionDecision(d: ProgressionDecision): Promise<void> {
  await db.progressionDecisions.put(d);
  await enqueueSyncEvent({
    userId: d.userId,
    entityType: "progression_decision",
    entityId: d.id,
    operation: "upsert",
    entityVersion: 1,
    payload: d,
  });
}

export async function listProgressionDecisions(): Promise<ProgressionDecision[]> {
  return db.progressionDecisions.orderBy("createdAt").toArray();
}

// ---------------------------- 训练序列模板（自定义训练计划） ----------------------------
export async function listTrainingTemplates(userId: string): Promise<TrainingTemplate[]> {
  return db.trainingTemplates.where("userId").equals(userId).sortBy("position");
}

export async function saveTrainingTemplate(template: TrainingTemplate): Promise<void> {
  const updated: TrainingTemplate = {
    ...template,
    version: template.version + 1,
    updatedAt: nowIso(),
  };
  await db.trainingTemplates.put(updated);
  await enqueueSyncEvent({
    userId: template.userId,
    entityType: "training_template",
    entityId: template.id,
    operation: "upsert",
    entityVersion: updated.version,
    payload: updated,
  });
}

export async function deleteTrainingTemplate(templateId: string, userId: string): Promise<void> {
  await db.trainingTemplates.delete(templateId);
  await enqueueSyncEvent({
    userId,
    entityType: "training_template",
    entityId: templateId,
    operation: "delete",
    entityVersion: 1,
    payload: { id: templateId },
  });
}

export async function resetTrainingTemplatesToDefault(userId: string): Promise<TrainingTemplate[]> {
  const defaults = createDefaultTemplates(userId);
  await db.transaction("rw", db.trainingTemplates, db.syncEvents, async () => {
    await db.trainingTemplates.where("userId").equals(userId).delete();
    await db.trainingTemplates.bulkPut(defaults);
    for (const t of defaults) {
      await enqueueSyncEvent({
        userId,
        entityType: "training_template",
        entityId: t.id,
        operation: "upsert",
        entityVersion: t.version,
        payload: t,
      });
    }
  });
  return defaults;
}

export async function reorderTrainingTemplates(templates: TrainingTemplate[], userId: string): Promise<void> {
  const updated = templates.map((t, idx) => ({
    ...t,
    position: idx + 1,
    version: t.version + 1,
    updatedAt: nowIso(),
  }));
  await db.transaction("rw", db.trainingTemplates, db.syncEvents, async () => {
    await db.trainingTemplates.bulkPut(updated);
    for (const t of updated) {
      await enqueueSyncEvent({
        userId,
        entityType: "training_template",
        entityId: t.id,
        operation: "upsert",
        entityVersion: t.version,
        payload: t,
      });
    }
  });
}

// ---------------------------- 通知设置（阶段 6：固定提醒按设置项持久化，不硬编码） ----------------------------
export const DEFAULT_NOTIFICATION_SETTING: NotificationSetting = {
  id: "notification-setting",
  userId: DEFAULT_PROFILE.id,
  enabled: false,
  fixedTime: "18:30",
  daysOfWeek: [],
  primaryDeviceId: null,
  restTimerNotifications: true,
  trainingReminderNotifications: true,
  timezone: "Asia/Shanghai",
  updatedAt: new Date().toISOString(),
};

export async function loadNotificationSetting(): Promise<NotificationSetting> {
  return getMetaJson<NotificationSetting>("notificationSettings", DEFAULT_NOTIFICATION_SETTING);
}

export async function saveNotificationSetting(s: NotificationSetting): Promise<void> {
  await setMeta("notificationSettings", { ...s, updatedAt: nowIso() });
}

// ---------------------------- 饮食修正偏好（阶段 5：常见餐食纠正可形成个人偏好） ----------------------------
export async function loadMealPrefs(max = 30): Promise<CorrectionPref[]> {
  const prefs = await getMetaJson<CorrectionPref[]>("mealPrefs", []);
  return aggregatePrefs(prefs, max);
}

/** 用户确认一条“已修正”餐食时保存偏好（同文本加权均值；最近 30 条内聚合去重）。 */
export async function saveMealPrefCorrection(entry: Pick<MealEntry, "rawText" | "finalCalories" | "finalProtein" | "finalFat" | "finalCarbs">): Promise<void> {
  if (!entry.rawText.trim()) return;
  const existing = await getMetaJson<CorrectionPref[]>("mealPrefs", []);
  const same = existing.find((p) => p.name === prefFromCorrection(entry).name);
  const updated = prefFromCorrection(entry, same);
  const rest = existing.filter((p) => p.name !== updated.name);
  await setMeta("mealPrefs", [updated, ...rest].slice(0, 30));
}

// ---------------------------- 同步事件队列（幂等，阶段 4 flush 上传） ----------------------------
export async function enqueueSyncEvent(input: {
  userId: string;
  entityType: string;
  entityId: string;
  operation: SyncOperation;
  entityVersion: number;
  payload: unknown;
}): Promise<void> {
  const id = uuid();
  await db.syncEvents.put({
    id,
    userId: input.userId,
    deviceId: "device-demo-1",
    entityType: input.entityType,
    entityId: input.entityId,
    operation: input.operation,
    entityVersion: input.entityVersion,
    payload: input.payload,
    clientCreatedAt: nowIso(),
    serverReceivedAt: null,
    syncStatus: "pending",
  });
}

export async function pendingSyncCount(): Promise<number> {
  return db.syncEvents.where("syncStatus").equals("pending").count();
}
